import _ from 'lodash'
import { mapByTime } from './getMapByTime'

import { ChannelEvent, ChartableStatsType, isSalesStats } from 'types'

/** Compute a correctly weighted average per time bucket from pre-aggregated sum/count columns. */
export const getWeightedAvgChartEvents = (
  data: ChannelEvent[][],
  sumField: keyof ChannelEvent,
  countField: keyof ChannelEvent,
): number[] => {
  const evt: number[] = []
  for (let i = 0; i < data.length; i++) {
    let totalSum = 0
    let totalCount = 0
    for (let j = 0; j < data[i].length; j++) {
      totalSum   += Number(data[i][j][sumField]   || 0)
      totalCount += Number(data[i][j][countField] || 0)
    }
    evt.push(totalCount > 0 ? _.round(totalSum / totalCount, 2) : 0)
  }
  return evt
}

export const getChartEvents = (data: ChannelEvent[][], type: ChartableStatsType) => {
  const evt: number[] = []

  for (let i = 0; i < data.length; i += 1) {
    let summ = 0
    for (let j = 0; j < data[i].length; j += 1) {
      if (isSalesStats(type)) {
        summ += Number(data[i][j][type] || 0) / 100
      } else {
        summ += Number(data[i][j][type]) || 0
      }
    }
    evt.push(_.round(summ, 2))
  }

  return evt
}

export const getChartData = (allEvents: ChannelEvent[], timezone: string) => {
  const mapedByDay = mapByTime(allEvents, 'day', timezone) || {}
  const mapedByWeek = mapByTime(allEvents, 'week', timezone) || {}
  const mapedByMonth = mapByTime(allEvents, 'month', timezone) || {}

  const dailyData = {
    views:        getChartEvents(Object.values(mapedByDay), 'views'),
    new_visitors: [] as number[],
    new_customers: getChartEvents(Object.values(mapedByDay), 'new_customers'),
    form_submits:  getChartEvents(Object.values(mapedByDay), 'form_submits'),
    leads:         getChartEvents(Object.values(mapedByDay), 'leads'),
    total_orders:  getChartEvents(Object.values(mapedByDay), 'total_orders'),
    total_amount:  getChartEvents(Object.values(mapedByDay), 'total_amount'),
    recurring_payments_amount: getChartEvents(Object.values(mapedByDay), 'recurring_payments_amount'),
    orders_amount: getChartEvents(Object.values(mapedByDay), 'orders_amount'),
    dates: Object.keys(mapedByDay),
  }

  const weeklyData = {
    views:        getChartEvents(Object.values(mapedByWeek), 'views'),
    new_visitors: [] as number[],
    new_customers: getChartEvents(Object.values(mapedByWeek), 'new_customers'),
    form_submits:  getChartEvents(Object.values(mapedByWeek), 'form_submits'),
    leads:         getChartEvents(Object.values(mapedByWeek), 'leads'),
    total_orders:  getChartEvents(Object.values(mapedByWeek), 'total_orders'),
    total_amount:  getChartEvents(Object.values(mapedByWeek), 'total_amount'),
    recurring_payments_amount: getChartEvents(Object.values(mapedByWeek), 'recurring_payments_amount'),
    orders_amount: getChartEvents(Object.values(mapedByWeek), 'orders_amount'),
    dates: Object.keys(mapedByWeek),
  }

  const monthlyData = {
    views:        getChartEvents(Object.values(mapedByMonth), 'views'),
    new_visitors: [] as number[],
    new_customers: getChartEvents(Object.values(mapedByMonth), 'new_customers'),
    form_submits:  getChartEvents(Object.values(mapedByMonth), 'form_submits'),
    leads:         getChartEvents(Object.values(mapedByMonth), 'leads'),
    total_orders:  getChartEvents(Object.values(mapedByMonth), 'total_orders'),
    total_amount:  getChartEvents(Object.values(mapedByMonth), 'total_amount'),
    recurring_payments_amount: getChartEvents(Object.values(mapedByMonth), 'recurring_payments_amount'),
    orders_amount: getChartEvents(Object.values(mapedByMonth), 'orders_amount'),
    dates: Object.keys(mapedByMonth),
  }

  const mappedByTime = {
    day: dailyData,
    week: weeklyData,
    month: monthlyData,
  }

  return mappedByTime
}
